import React, { useState, useEffect } from 'react';
import { X, Receipt, Check, AlertCircle, RefreshCw, Search } from 'lucide-react';
import { useAncillaryFees, useAssignToStudents } from '../../hooks/useAncillaryFees';
import { getAcademicYears } from '@/services/academicManagementService';
import { formatCurrency } from '@/services/tuitionService';

interface Props {
  studentId: string;
  studentName: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface FilterOption {
  value: string;
  label: string;
  isCurrent?: boolean;
}

const AssignAncillaryFeeModal: React.FC<Props> = ({ studentId, studentName, onClose, onSuccess }) => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFees, setSelectedFees] = useState<string[]>([]);
  const [academicYears, setAcademicYears] = useState<FilterOption[]>([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [isLoadingYears, setIsLoadingYears] = useState(true);

  // Fetch active ancillary fees
  const { data: feesData, isLoading: isLoadingFees } = useAncillaryFees({ includeArchived: false });
  const assignMutation = useAssignToStudents();

  const fees = feesData?.data?.filter(f => f.isActive) || [];

  // Filter fees by search
  const filteredFees = fees.filter(fee =>
    fee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    fee.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Load academic years
  useEffect(() => {
    const loadYears = async () => {
      try {
        const years = await getAcademicYears();
        setAcademicYears(years);
        const currentYear = years.find(y => y.isCurrent);
        if (currentYear) {
          setSelectedAcademicYear(currentYear.value);
        }
      } catch (err) {
        console.error('Error loading academic years:', err);
      } finally {
        setIsLoadingYears(false);
      }
    };
    loadYears();
  }, []);

  const toggleFeeSelection = (feeId: string) => {
    setSelectedFees(prev =>
      prev.includes(feeId)
        ? prev.filter(id => id !== feeId)
        : [...prev, feeId]
    );
  };

  const handleAssign = async () => {
    if (selectedFees.length === 0) {
      setError('Veuillez sélectionner au moins un frais annexe');
      return;
    }

    if (!selectedAcademicYear) {
      setError('Veuillez sélectionner une année académique');
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      // Assign each selected fee to the student
      for (const feeId of selectedFees) {
        await assignMutation.mutateAsync({
          ancillaryFeeId: feeId,
          studentIds: [studentId],
          academicYearId: selectedAcademicYear,
        });
      }

      setSuccess(`${selectedFees.length} frais annexe(s) affecté(s) à ${studentName}`);
      setSelectedFees([]);

      // Call onSuccess after a short delay to show the success message
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'affectation');
    }
  };

  const calculateTotal = () => {
    return selectedFees.reduce((total, feeId) => {
      const fee = fees.find(f => f.id === feeId);
      return total + (fee?.amount || 0);
    }, 0);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Receipt className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Affecter un frais annexe
              </h2>
              <p className="text-sm text-gray-500">
                {studentName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Messages */}
          {error && (
            <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle size={18} />
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              <Check size={18} />
              {success}
            </div>
          )}

          {/* Academic Year Selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Année académique
            </label>
            <select
              value={selectedAcademicYear}
              onChange={(e) => setSelectedAcademicYear(e.target.value)}
              disabled={isLoadingYears}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
            >
              {isLoadingYears ? (
                <option>Chargement...</option>
              ) : (
                <>
                  <option value="">Sélectionner</option>
                  {academicYears.map((year) => (
                    <option key={year.value} value={year.value}>
                      {year.label} {year.isCurrent ? '(En cours)' : ''}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Rechercher un frais..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>

          {/* Fees List */}
          {isLoadingFees ? (
            <div className="text-center py-8">
              <RefreshCw className="mx-auto h-6 w-6 text-gray-400 animate-spin" />
              <p className="mt-2 text-sm text-gray-500">Chargement des frais...</p>
            </div>
          ) : filteredFees.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {filteredFees.map((fee) => (
                <label
                  key={fee.id}
                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedFees.includes(fee.id)
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedFees.includes(fee.id)}
                      onChange={() => toggleFeeSelection(fee.id)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{fee.name}</p>
                      {fee.description && (
                        <p className="text-xs text-gray-500">{fee.description}</p>
                      )}
                    </div>
                  </div>
                  <span className="font-semibold text-gray-900 text-sm">
                    {formatCurrency(fee.amount)}
                  </span>
                </label>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Receipt className="mx-auto h-8 w-8 text-gray-300 mb-2" />
              <p className="text-sm">Aucun frais annexe disponible</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          {selectedFees.length > 0 && (
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-600">
                {selectedFees.length} frais sélectionné(s)
              </span>
              <span className="text-lg font-bold text-gray-900">
                {formatCurrency(calculateTotal())}
              </span>
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleAssign}
              disabled={assignMutation.isPending || selectedFees.length === 0 || !selectedAcademicYear}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {assignMutation.isPending ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Affectation...
                </>
              ) : (
                <>
                  <Check size={16} />
                  Affecter
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignAncillaryFeeModal;
